import { useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { resolveWarehouseLocation } from '../utils/locationInventory'
import { getAdminId } from '../utils/getAdminId'
import {
  generateProFormaInvoiceHTML,
  htmlToPdfBase64,
  buildEpcQrDataUrl,
  type SupplierProfile,
} from '../lib/pdfGenerator'
import { sendB2BInvoiceUpdatedEmail, type EmailAttachment } from '../lib/b2bEmail'
import { filterCcVsPrimary } from '../utils/emailRecipients'
import type { Invoice, InvoiceItem } from '../types/invoice'
import type { B2BBuyer } from '../types/b2b'

export interface AdminInvoiceLineInput {
  product_id: string
  quantity: number
  product_name?: string
  sku?: string
  color_name?: string
  size_name?: string
}

export interface AdminInvoiceRow extends Invoice {
  invoice_items: InvoiceItem[]
  reservation?: {
    id: string
    status: string
    reservation_number: string
    reservation_source?: string
    pickup_scheduled_date?: string
  } | null
}

export interface InvoiceEditContext {
  reservation: { id: string; status: string; reservation_number: string } | null
  warehouseLocationId: string | null
  // product_id -> warehouse stock at the warehouse location
  warehouseQty: Record<string, number>
  // product_id -> qty reserved by OTHER active reservations (excludes this one)
  reservedByOthers: Record<string, number>
}

const ACTIVE_STATUSES = ['reserved', 'ready']
// PostgREST caps a single response at 1000 rows. Tenants routinely have more
// warehouse inventory / active reservation rows than that, so every read that
// must be COMPLETE for a correct availability number is paged in 1000-row windows.
const PAGE_SIZE = 1000

function toNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? 0))
  return Number.isFinite(n) ? n : 0
}

export function useB2BAdminInvoices() {
  const { user } = useAuth()
  const [invoices, setInvoices] = useState<AdminInvoiceRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadInvoicesForBuyer = useCallback(
    async (buyerId: string): Promise<void> => {
      if (!user || !buyerId) return
      const adminId = getAdminId(user)
      if (!adminId) return
      setLoading(true)
      setError(null)
      try {
        const { data, error: fetchError } = await supabase
          .from('invoices')
          .select(
            '*, invoice_items(*), reservation:reservations(id, status, reservation_number, reservation_source, pickup_scheduled_date)',
          )
          .eq('admin_id', adminId)
          .eq('buyer_id', buyerId)
          .eq('is_pro_forma', true)
          .order('invoice_date', { ascending: false })

        if (fetchError) throw fetchError

        const rows: AdminInvoiceRow[] = (data ?? []).map((inv: Record<string, unknown>) => ({
          ...(inv as unknown as Invoice),
          subtotal_amount: toNumber(inv.subtotal_amount),
          tax_amount: toNumber(inv.tax_amount),
          total_amount: toNumber(inv.total_amount),
          account_discount: inv.account_discount != null ? toNumber(inv.account_discount) : undefined,
          invoice_items: ((inv.invoice_items as InvoiceItem[]) ?? []).map(it => ({
            ...it,
            quantity: toNumber(it.quantity),
            unit_price: toNumber(it.unit_price),
            total_price: toNumber(it.total_price),
            base_unit_price: it.base_unit_price != null ? toNumber(it.base_unit_price) : undefined,
            vat_rate: toNumber(it.vat_rate),
            vat_amount: toNumber(it.vat_amount),
          })),
          reservation: (inv.reservation as AdminInvoiceRow['reservation']) ?? null,
        }))

        setInvoices(rows)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invoices')
        setInvoices([])
      } finally {
        setLoading(false)
      }
    },
    [user],
  )

  // An invoice is editable only when its linked reservation is still active.
  const isInvoiceEditable = useCallback((invoice: AdminInvoiceRow): boolean => {
    return !!invoice.reservation_id && !!invoice.reservation && ACTIVE_STATUSES.includes(invoice.reservation.status)
  }, [])

  const loadEditContext = useCallback(
    async (invoice: AdminInvoiceRow): Promise<InvoiceEditContext> => {
      const empty: InvoiceEditContext = {
        reservation: null,
        warehouseLocationId: null,
        warehouseQty: {},
        reservedByOthers: {},
      }
      if (!user || !invoice.reservation_id) return empty
      const adminId = getAdminId(user)
      if (!adminId) return empty

      const warehouse = await resolveWarehouseLocation(adminId)
      const warehouseLocationId = warehouse?.id ?? null

      // Warehouse stock for EVERY product the editor may show or search. Paged so
      // tenants with > 1000 warehouse rows do not silently lose products (which
      // previously rendered them as "max 0" and hid them from the add-product search).
      const warehouseQty: Record<string, number> = {}
      if (warehouseLocationId) {
        for (let from = 0; ; from += PAGE_SIZE) {
          const { data, error: whError } = await supabase
            .from('product_location_inventory')
            .select('product_id, quantity')
            .eq('admin_id', adminId)
            .eq('location_id', warehouseLocationId)
            .order('product_id', { ascending: true })
            .range(from, from + PAGE_SIZE - 1)
          if (whError) throw whError
          const rows = (data ?? []) as { product_id: string; quantity: number }[]
          for (const row of rows) warehouseQty[row.product_id] = toNumber(row.quantity)
          if (rows.length < PAGE_SIZE) break
        }
      }

      // Stock held by OTHER active reservations. Also paged: a tenant can exceed
      // 1000 active reservation lines, and an undercount here would overstate
      // availability and risk overselling.
      const reservedByOthers: Record<string, number> = {}
      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error: reservedError } = await supabase
          .from('reservation_items')
          .select('product_id, quantity, reservations!inner(id, admin_id, status)')
          .eq('reservations.admin_id', adminId)
          .in('reservations.status', ACTIVE_STATUSES)
          .order('id', { ascending: true })
          .range(from, from + PAGE_SIZE - 1)
        if (reservedError) throw reservedError
        const rows = (data as Array<Record<string, unknown>>) ?? []
        for (const row of rows) {
          const resv = row.reservations as { id?: string } | null
          if (resv?.id === invoice.reservation_id) continue // exclude this reservation
          const pid = row.product_id as string
          reservedByOthers[pid] = (reservedByOthers[pid] ?? 0) + toNumber(row.quantity)
        }
        if (rows.length < PAGE_SIZE) break
      }

      return {
        reservation: invoice.reservation
          ? { id: invoice.reservation.id, status: invoice.reservation.status, reservation_number: invoice.reservation.reservation_number }
          : null,
        warehouseLocationId,
        warehouseQty,
        reservedByOthers,
      }
    },
    [user],
  )

  const saveInvoice = useCallback(
    async (params: {
      invoiceId: string
      divisor: number
      vatPct: number
      sourceLocationId: string
      items: AdminInvoiceLineInput[]
    }): Promise<{ success: boolean; error?: string }> => {
      if (!user) return { success: false, error: 'Not authenticated' }
      const adminId = getAdminId(user)
      if (!adminId) return { success: false, error: 'No active company selected' }
      const { invoiceId, divisor, vatPct, sourceLocationId, items } = params
      try {
        const { error: rpcError } = await supabase.rpc('admin_update_b2b_invoice', {
          p_invoice_id: invoiceId,
          p_admin_id: adminId,
          p_divisor: divisor,
          p_vat_pct: vatPct,
          p_source_location_id: sourceLocationId,
          p_items: items,
        })
        // supabase.rpc resolves with a PostgrestError (a plain object, NOT an Error
        // instance), so surface its message/details/hint directly instead of letting
        // it fall through to the generic fallback with no reason for the user.
        if (rpcError) {
          const reason = rpcError.message || rpcError.details || rpcError.hint
          return { success: false, error: reason || 'Failed to save invoice' }
        }
        return { success: true }
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to save invoice' }
      }
    },
    [user],
  )

  const prepareInvoiceDocument = useCallback(
    async (
      invoice: AdminInvoiceRow,
      buyer: B2BBuyer,
    ): Promise<{ html: string; supplier: SupplierProfile; inv: Invoice }> => {
      if (!user) throw new Error('Not authenticated')
      const adminId = getAdminId(user)
      if (!adminId) throw new Error('No active company selected')

      const { data: supplierData } = await supabase
        .from('users')
        .select(
          'company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id, iban, bic, bank_name, logo_url, payment_terms_days',
        )
        .eq('id', adminId)
        .single()

      const supplier = (supplierData ?? {}) as SupplierProfile

      const inv: Invoice = {
        ...invoice,
        user_id: invoice.user_id || user.id,
        subtotal_amount: toNumber(invoice.subtotal_amount),
        tax_amount: toNumber(invoice.tax_amount),
        total_amount: toNumber(invoice.total_amount),
        invoice_items: (invoice.invoice_items || []).map(it => ({
          ...it,
          unit_price: toNumber(it.unit_price),
          total_price: toNumber(it.total_price),
        })),
      }

      const epcQr = await buildEpcQrDataUrl(supplier, inv.total_amount, inv.invoice_number)
      const html = generateProFormaInvoiceHTML(inv, buyer, supplier, epcQr)
      return { html, supplier, inv }
    },
    [user],
  )

  const buildInvoiceHtml = useCallback(
    async (invoice: AdminInvoiceRow, buyer: B2BBuyer): Promise<string> => {
      const { html } = await prepareInvoiceDocument(invoice, buyer)
      return html
    },
    [prepareInvoiceDocument],
  )

  const resendInvoice = useCallback(
    async (invoice: AdminInvoiceRow, buyer: B2BBuyer): Promise<{ success: boolean; error?: string }> => {
      if (!user) return { success: false, error: 'Not authenticated' }
      const adminId = getAdminId(user)
      if (!adminId) return { success: false, error: 'No active company selected' }
      try {
        const { html, supplier, inv } = await prepareInvoiceDocument(invoice, buyer)
        const pdfBase64 = await htmlToPdfBase64(html, { scale: 1.5, jpegQuality: 0.85 })

        const attachments: EmailAttachment[] = [
          { filename: `Invoice-${inv.invoice_number}.pdf`, content: pdfBase64, contentType: 'application/pdf', encoding: 'base64' },
        ]

        const { data: adminRow } = await supabase
          .from('users')
          .select('reservation_pdf_cc_emails')
          .eq('id', adminId)
          .single()
        const rawCc = Array.isArray(adminRow?.reservation_pdf_cc_emails)
          ? (adminRow!.reservation_pdf_cc_emails as string[]).filter(
              (e): e is string => typeof e === 'string' && e.trim().length > 0,
            )
          : []
        const cc = filterCcVsPrimary(rawCc, buyer.email)

        await sendB2BInvoiceUpdatedEmail(
          buyer,
          { invoice_number: inv.invoice_number, total_amount: inv.total_amount },
          {
            company_name: supplier.company_name,
            company_address: supplier.company_address,
            company_phone: supplier.company_phone,
            company_email: supplier.company_email,
            vat_number: supplier.vat_number,
          },
          attachments,
          cc.length > 0 ? cc : undefined,
          adminId,
          invoice.reservation_id,
        )

        return { success: true }
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to resend invoice' }
      }
    },
    [user, prepareInvoiceDocument],
  )

  return {
    invoices,
    loading,
    error,
    loadInvoicesForBuyer,
    isInvoiceEditable,
    loadEditContext,
    saveInvoice,
    buildInvoiceHtml,
    resendInvoice,
  }
}
